import { useEffect, useState } from "react";
import { FileRepository } from "@amityco/ts-sdk";

import { CloseMD } from "@/icons";
import { Loader } from "@/components";
import { cn } from "@/utils";

interface AttachmentPreviewProps {
  file?: Amity.File<"image" | "video">;
  onRemove?: () => void;
  pending?: {
    progress: number;
    file: File;
  };
}

export const AttachmentPreview = ({
  file,
  onRemove,
  pending,
}: AttachmentPreviewProps) => {
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);

  useEffect(() => {
    if (pending?.file.type.includes("video")) {
      setVideoUrl(URL.createObjectURL(pending.file));
      return;
    }

    if (pending?.file.type.includes("image")) {
      setThumbnailUrl(URL.createObjectURL(pending.file));
      return;
    }

    if (file?.type === "image") {
      const mediumUrl = FileRepository.fileUrlWithSize(file.fileUrl, "medium");
      setThumbnailUrl(mediumUrl);
      return;
    }

    if (file?.type === "video") {
      setVideoUrl(file.fileUrl);
      return;
    }
  }, [file, pending?.file]);

  return (
    <div
      className={cn(
        "shrink-1 group relative rounded-lg bg-surface-thumbnail",
        !thumbnailUrl && "aspect-video",
      )}
    >
      {videoUrl ? (
        <video src={videoUrl} className="rounded-lg" />
      ) : thumbnailUrl && pending ? (
        <img
          className="h-full rounded-lg"
          src={thumbnailUrl}
          alt="Attachment Preview"
        />
      ) : thumbnailUrl ? (
        // Use file attributes to display grey placeholder while image is loading
        <div
          className="h-full rounded-lg"
          style={{
            aspectRatio:
              file?.type === "image" && file.attributes?.metadata
                ? `${file.attributes.metadata.width} / ${file.attributes.metadata.height}`
                : "16 / 9",
          }}
        >
          <img
            className="h-full rounded-lg"
            src={thumbnailUrl}
            alt="Attachment Preview"
          />
        </div>
      ) : null}
      {pending ? (
        <div className="p2-medium absolute inset-0 flex flex-col items-center justify-center gap-1 rounded-lg bg-surface-overlay-50 pt-[22px] text-item-invert-primary">
          <Loader className="h-8 w-8" />
          <div>{pending.progress}%</div>
        </div>
      ) : (
        <div
          className="absolute right-1 top-1 cursor-pointer rounded-md bg-surface-overlay-80 p-0.5 text-item-invert-primary opacity-0 transition-opacity duration-100 hover:opacity-90 group-hover:opacity-100"
          onClick={onRemove}
        >
          <CloseMD width={16} height={16} />
        </div>
      )}
    </div>
  );
};
